import { ReactNode, useMemo } from 'react';
import { ActionsIcon } from '../../../../../assets/svg/actions';
import { InfoIcon } from '../../../../../assets/svg/info';
import { PlusIcon } from '../../../../../assets/svg/plus';
import { Button } from '../../../../../components/button/button';
import { Loader } from '../../../../../components/loader/loader';
import { Page } from '../../../../../components/page/page';
import { DeleteModal } from '../../../../../features/delete-modal/delete-modal';
import { Table } from '../../../../../features/table/table';
import { Tooltip } from '../../../../../features/tooltip/tooltip';
import { RoleType } from '../../../../../store/api/role-api';
import { RolesDictionaryCreateModal } from '../../staff/roles-dictionary/roles-dictionary-create-modal/roles-dictionary-create-modal';
import { CompanyAdmins } from '../company-settings/company-admins/company-admins';
import { RolesAndAccessButton } from './roles-and-access-button/roles-and-access-button';
import s from './roles-and-access.module.css';
import { useRolesAndAccess } from './use-roles-and-access';

const colWidth = 100;
const cols: Record<string, { label?: string; width?: number }> = {
  name: {
    label: 'Имя',
    width: 200,
  },
  dashboard: {
    label: 'Дашборд',
    width: colWidth,
  },
  monitoring: {
    label: 'Мониторинг',
    width: colWidth,
  },
  qr: {
    label: 'QR-коды',
    width: colWidth,
  },
  review: {
    label: 'Отзывы',
    width: colWidth,
  },
  tree: {
    label: 'Деревья',
    width: colWidth,
  },
  task: {
    label: 'Задачи',
    width: colWidth,
  },
  staff: {
    label: 'Стафф',
    width: colWidth,
  },
  menu: {
    label: 'Меню',
    width: colWidth,
  },
  lead: {
    label: 'Лиды',
    width: colWidth,
  },
  promotion: {
    label: 'Акции',
    width: colWidth,
  },
  form: {
    label: 'Формы',
    width: colWidth,
  },
  device: {
    label: 'Устройства',
    width: colWidth,
  },
  profile: {
    label: 'Профиль',
    width: colWidth,
  },
};

export const RolesAndAccess = () => {
  const {
    isLoading,
    t,
    roles = [],
    putRole,
    createRoleModal,
    deleteRole,
    addQueryParams,
    managerProfile,
  } = useRolesAndAccess();

  const { withLang, withButtons } = useMemo(() => {
    const withLang = Object.entries(cols).reduce((acc, [key, value]) => {
      acc[key] = {
        ...value,
        label: t(value.label || key),
      };
      return acc;
    }, {} as Record<string, { label?: string; width?: number }>);

    const withButtons: Record<string, ReactNode>[] = roles.map((item) => {
      const keys = Object.keys(cols);
      return keys.reduce((acc, key) => {
        const permissionKey = key as keyof RoleType['permissions'];

        return {
          ...acc,
          [permissionKey]:
            key === 'name' ? (
              <span key={item.id} className={s.name}>
                {item.name}
                <Tooltip trigger={<ActionsIcon />} closeOnContentClick>
                  <ul className={s.list}>
                    <li
                      className={s.item}
                      onClick={() => createRoleModal.handleOpenModal({ roleId: item.id })}
                    >
                      {t('Редактировать')}
                    </li>
                    <li
                      className={s.item}
                      onClick={() =>
                        addQueryParams({
                          deleteId: item.id,
                          deleteName: item.name,
                          deletePrefix: 'role',
                        })
                      }
                    >
                      {t('Удалить')}
                    </li>
                  </ul>
                </Tooltip>
              </span>
            ) : (
              <RolesAndAccessButton
                key={item.id}
                permission={item.permissions[permissionKey]}
                onChange={(e) =>
                  putRole({ ...item, permissions: { ...item.permissions, [permissionKey]: e } })
                }
              />
            ),
        };
      }, {});
    });
    return { withButtons, withLang };
  }, [addQueryParams, createRoleModal, putRole, roles, t]);

  if (managerProfile?.role !== 'admin_head') {
    return null;
  }

  return (
    <Page className={s.page}>
      <h2>{t('Роли и доступы')}</h2>

      <DeleteModal onSubmit={deleteRole} prefix="role" />

      <CompanyAdmins />

      {createRoleModal.isOpen && (
        <RolesDictionaryCreateModal onClose={() => createRoleModal.handleCloseModal(['roleId'])} />
      )}

      {isLoading && <Loader />}

      <div className={s.header}>
        <div className={s.title}>
          <h3>{t('Настройка доступов')}</h3>
          <h4>{t('Для изменения доступа просто кликните на необходимую ячейку')}</h4>
        </div>

        <div className={s.controls}>
          <Button size="lg" fullWidth onClick={() => createRoleModal.handleOpenModal()}>
            <PlusIcon />
            {t('Добавить роль')}
          </Button>
          <Tooltip
            triggerWrapperClassName={s.trigger}
            trigger={
              <>
                <InfoIcon />
                {t('Что означают иконки?')}
              </>
            }
          >
            <ul className={s.info}>
              <li className={s.item}>
                <RolesAndAccessButton permission="write" onChange={() => {}} />
                {t('Запись')}
              </li>
              <li className={s.item}>
                <RolesAndAccessButton permission="read" onChange={() => {}} />
                {t('Чтение')}
              </li>
              <li className={s.item}>
                <RolesAndAccessButton onChange={() => {}} />
                {t('Нет доступа')}
              </li>
            </ul>
          </Tooltip>
        </div>
      </div>
      <Table className={s.table} cols={withLang} rows={withButtons} />
    </Page>
  );
};
